import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SkyViewer from '../SkyViewer.jsx';
import TutorialMap from './TutorialMap.jsx';
import { visibleStars, visibleConstellations, visiblePlanets } from '../roundGenerator.mjs';
import { LESSONS, EXPLORER_START, dayLabel, explorerDate } from './lessons.mjs';
import { drawGuides } from './guides.mjs';
import { sunAltitude, sunHorizontal } from '../astronomy.mjs';
import { daylightFactor } from '../skyRender.mjs';
import { localSolarTime } from '../format.mjs';

/*
 * A brighter cut of the catalogue than the game uses. The lessons are about
 * picking out a handful of specific stars, and five thousand of them at
 * magnitude 6 makes the pointers impossible to follow. It also keeps the sky
 * cheap enough to recompute every frame while the observer is moving.
 */
const TUTORIAL_MAG_LIMIT = 4.8;

// The Moon is kept off the tutorial sky entirely: its glare washes out faint
// stars, and every lesson is about picking out a specific shape.
const NO_MOON = { alt: -90, az: 0, illuminated: 0, waxing: true };

// How long the sky takes to travel to a newly picked place, in milliseconds.
// Long enough to read as a journey, short enough not to be a wait.
const TRAVEL_MS = 900;

// Shortest way round: dragging from Tokyo to San Francisco should cross the
// Pacific, not run backwards over Europe.
function shortestLonStep(from, to) {
  return ((to - from + 540) % 360) - 180;
}

export default function TutorialView({ catalog, figures, rings, onExit, onPlay }) {
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(true);
  const startedAt = useRef(performance.now());

  const lesson = LESSONS[index];

  /*
   * The explorer's observer. `target` is where the pin has been dragged to and
   * `here` is where the sky has got to, eased toward it — the drag would
   * otherwise teleport, and watching the pole climb as you travel is the whole
   * point of the page.
   */
  const [target, setTarget] = useState(() => ({ lat: EXPLORER_START.lat, lon: EXPLORER_START.lon }));
  const [here, setHere] = useState(() => ({ lat: EXPLORER_START.lat, lon: EXPLORER_START.lon }));
  const [clock, setClock] = useState(() => ({
    dayOfYear: EXPLORER_START.dayOfYear,
    hour: EXPLORER_START.hour
  }));
  const [explorerView, setExplorerView] = useState(() => LESSONS[LESSONS.length - 1].camera(0));

  useEffect(() => {
    if (here.lat === target.lat && here.lon === target.lon) return undefined;
    const from = here;
    const step = shortestLonStep(from.lon, target.lon);
    const startedTravel = performance.now();
    let frame;
    const travel = () => {
      const t = Math.min(1, (performance.now() - startedTravel) / TRAVEL_MS);
      const k = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
      setHere({
        lat: from.lat + (target.lat - from.lat) * k,
        lon: ((from.lon + step * k + 540) % 360) - 180
      });
      if (t < 1) frame = requestAnimationFrame(travel);
    };
    frame = requestAnimationFrame(travel);
    return () => cancelAnimationFrame(frame);
    // `here` deliberately absent: this restarts on a new target, not every frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  const brightCatalog = useMemo(
    () => catalog.filter((star) => star[2] <= TUTORIAL_MAG_LIMIT),
    [catalog]
  );

  useEffect(() => {
    startedAt.current = performance.now();
    setProgress(0);
  }, [index]);

  useEffect(() => {
    // The explorer is driven by the pointer, not by a clock of its own.
    if (!playing || lesson.interactive) return undefined;
    let frame;
    const tick = () => {
      const elapsed = performance.now() - startedAt.current;
      const raw = elapsed / lesson.duration;
      setProgress(lesson.loop ? raw % 1 : Math.min(1, raw));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [lesson, playing]);

  const explorerState = {
    lat: here.lat,
    lon: here.lon,
    date: explorerDate(clock)
  };

  const state = lesson.interactive ? explorerState : lesson.state(progress);
  const view = lesson.interactive ? explorerView : lesson.camera(progress);
  /*
   * Why the sky here is not usable, which is not the same question as whether
   * the Sun is up.
   *
   * `daylightFactor` starts fading the stars at -18 degrees and has them gone by
   * -3, so there is an eighteen-degree band at each end of the night where the
   * Sun is genuinely below the horizon, the map's shadow correctly covers the
   * pin, and the sky is still too washed out to navigate by. At Denver in
   * September that band is about an hour and a half long. Announcing "the Sun is
   * up" through all of it told the reader something the map beside it plainly
   * contradicted.
   *
   * Tied to `daylightFactor` rather than to the threshold directly, so the
   * message appears exactly when the sky actually starts going out.
   */
  const sunAlt = lesson.interactive
    ? sunAltitude(state.lat, state.lon, state.date)
    : null;
  const washedOut = sunAlt !== null && daylightFactor(sunAlt) > 0;
  const sunRisen = sunAlt !== null && sunAlt >= 0;

  /*
   * The sky is recomputed from the observer for every frame, through the same
   * transforms the game uses. That is the point of the tutorial: when Polaris
   * climbs as the latitude rises, it climbs because the astronomy says so.
   */
  const round = useMemo(
    () => ({
      lat: state.lat,
      lon: state.lon,
      date: state.date,
      moon: NO_MOON,
      /*
       * The real Sun, unlike the Moon. Every scripted lesson is set in
       * astronomical night so this changes nothing for them, but the explorer
       * can be dragged into daylight, and there the sky has to actually go out.
       */
      sun: sunHorizontal(state.lat, state.lon, state.date),
      stars: visibleStars(brightCatalog, state.date, state.lat, state.lon),
      planets: visiblePlanets(state.date, state.lat, state.lon),
      constellations: visibleConstellations(figures, state.date, state.lat, state.lon)
    }),
    [brightCatalog, figures, state.lat, state.lon, state.date]
  );

  const overlay = useCallback(
    (ctx, camera, dpr) => drawGuides(ctx, camera, dpr, state, lesson.guides),
    [state.lat, state.lon, state.date, lesson]
  );

  const go = (next) => {
    if (next < 0 || next >= LESSONS.length) return;
    setIndex(next);
    setPlaying(true);
  };

  return (
    <div className="sg-tutorial">
      <div className="sg-stage sg-tutorial-stage">
        <SkyViewer
          round={round}
          texture={null}
          view={view}
          onViewChange={lesson.interactive ? setExplorerView : () => {}}
          overlay={overlay}
          showHint={false}
          canPan={Boolean(lesson.interactive)}
          guideMode
        />

        <p className="sg-tutorial-caption">{lesson.caption(progress, state)}</p>

        <TutorialMap
          rings={rings}
          lat={state.lat}
          lon={state.lon}
          date={state.date}
          name={state.name}
          onPick={lesson.interactive ? setTarget : null}
        />

        {lesson.interactive && (
          <div className="sg-explorer">
            <label className="sg-explorer-row">
              <span className="sg-explorer-label">Time</span>
              <input
                type="range"
                min="0"
                max="23.75"
                step="0.25"
                value={clock.hour}
                onChange={(event) =>
                  setClock((prev) => ({ ...prev, hour: Number(event.target.value) }))
                }
                aria-label="Time of day"
              />
              <span className="sg-explorer-value">{localSolarTime(state.date, state.lon)}</span>
            </label>
            <label className="sg-explorer-row">
              <span className="sg-explorer-label">Date</span>
              <input
                type="range"
                min="0"
                max="364"
                step="1"
                value={clock.dayOfYear}
                onChange={(event) =>
                  setClock((prev) => ({ ...prev, dayOfYear: Number(event.target.value) }))
                }
                aria-label="Day of year"
              />
              <span className="sg-explorer-value">{dayLabel(explorerDate(clock))}</span>
            </label>
            {/*
              Not an error state. Dragging into daylight empties the sky because
              it would, and seeing that happen is worth more than preventing it.
            */}
            {washedOut && (
              <p className="sg-explorer-daylight">
                {sunRisen
                  ? 'The Sun is up here — slide the clock, or move somewhere in the shadow.'
                  : 'Not dark enough here yet — the Sun is down but the sky is still lit. Slide the clock, or move deeper into the shadow.'}
              </p>
            )}
          </div>
        )}

        <ol className="sg-tutorial-dots" aria-label="Lessons">
          {LESSONS.map((entry, i) => (
            <li key={entry.id}>
              <button
                type="button"
                className={i === index ? 'is-current' : ''}
                aria-current={i === index}
                aria-label={entry.title}
                onClick={() => go(i)}
              />
            </li>
          ))}
        </ol>
      </div>

      <aside className="sg-tutorial-text">
        <p className="sg-tutorial-step">
          Lesson {index + 1} <span className="sg-muted">of {LESSONS.length}</span>
        </p>
        <h2>{lesson.title}</h2>
        {/* A blurb may be one string or several paragraphs. */}
        {(Array.isArray(lesson.blurb) ? lesson.blurb : [lesson.blurb]).map((para, i) => (
          <p key={i} className="sg-tutorial-blurb">
            {para}
          </p>
        ))}

        <div className="sg-tutorial-nav">
          <button type="button" className="sg-secondary" disabled={index === 0} onClick={() => go(index - 1)}>
            Back
          </button>
          {index + 1 < LESSONS.length ? (
            <button type="button" className="sg-primary" onClick={() => go(index + 1)}>
              Next
            </button>
          ) : (
            <button type="button" className="sg-primary sg-wordmark" onClick={onPlay}>
              Play StarGuessr
            </button>
          )}
        </div>

        <button type="button" className="sg-tutorial-skip" onClick={onExit}>
          Back to the start
        </button>
      </aside>
    </div>
  );
}
